/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import './index.less';
import './index.en.less';
import arrowGif from '../../../../../../assets/2025/arrow.gif';

export interface IExpandableListItem {
  title: string;
  content: string;
}

export interface IExpandableListIllustrationProps {
  mainTitle?: string;
  subTitle?: string;
  description?: string;
  illustration: React.ReactNode;
  items: IExpandableListItem[];
  defaultOpenIndex?: number;
  id?: string;
  onComplete?: () => void;
  showGuideArrow?: boolean;
}

const ExpandableListIllustration: React.FunctionComponent<IExpandableListIllustrationProps> = ({
  mainTitle,
  subTitle,
  description,
  illustration,
  items,
  defaultOpenIndex = -1,
  id,
  onComplete,
  showGuideArrow = true,
}) => {
  const [openIndex, setOpenIndex] = useState<number>(showGuideArrow ? -1 : defaultOpenIndex);
  const [completedIndexes, setCompletedIndexes] = useState<Set<number>>(new Set());
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    setCompletedIndexes(new Set());
    setOpenIndex(showGuideArrow ? -1 : defaultOpenIndex);
    hasCompletedRef.current = false;
  }, [items.length]);

  useEffect(() => {
    if (
      items.length > 0 &&
      completedIndexes.size === items.length &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      onComplete?.();
    }
  }, [completedIndexes, items, onComplete]);

  const handleItemClick = (index: number) => {
    if (showGuideArrow) {
      const isCompleted = completedIndexes.has(index);
      const isNextTarget = index === completedIndexes.size;
      if (!isCompleted && !isNextTarget) return;
      if (isNextTarget) {
        const newCompleted = new Set(completedIndexes);
        newCompleted.add(index);
        setCompletedIndexes(newCompleted);
        setOpenIndex(index);
      } else {
        setOpenIndex(openIndex === index ? -1 : index);
      }
      return;
    }
    setOpenIndex(openIndex === index ? -1 : index);
    if (onComplete) {
      onComplete();
    }
  };

  const arrowTargetIndex = showGuideArrow ? completedIndexes.size : -1;

  return (
    <div className="expandable-list-illustration" id={id}>
      <div className="eli-content">
        <div className="eli-text-section">
          {mainTitle && <h2 className="eli-main-title">{mainTitle}</h2>}
          {subTitle && <h3 className="eli-sub-title">{subTitle}</h3>}
          {description && <p className="eli-description">{description}</p>}
          <div className="eli-list">
            {items.map((item, index) => {
              const isOpen = openIndex === index;
              const showArrow = showGuideArrow && index === arrowTargetIndex;
              return (
                <div key={index} className={`eli-list-item ${isOpen ? 'is-open' : ''}`}>
                  <button
                    className="eli-list-item-header"
                    onClick={() => handleItemClick(index)}
                  >
                    <span className="eli-list-item-title">{item.title}</span>
                    <span className="eli-list-item-arrow">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </button>
                  <div className="eli-list-item-content">
                    <div className="eli-list-item-content-inner">
                      <p>{item.content}</p>
                    </div>
                  </div>
                  {showArrow && (
                    <img src={arrowGif} alt="" className="eli-guide-arrow" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="eli-illustration">
          {illustration}
        </div>
      </div>
    </div>
  );
};

export default ExpandableListIllustration;
